import { useState } from "react";
import { Save, ShieldAlert, Weight, Activity, Wine, Flame, HeartPulse, Info, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  computeBMR,
  computeTDEE,
  computeHydration,
  computeMacroTargets,
  computeAlcohol,
  ACTIVITY_LEVELS,
  ALCOHOL_TYPES,
  MACRO_AREAS,
} from "@/lib/mbNutrition";

const COMMON_ALLERGENS = [
  "Glutine",
  "Lattosio",
  "Noci / Frutta a guscio",
  "Crostacei",
  "Uova",
  "Soia",
  "Pesce",
  "Arachidi",
  "Sesamo",
  "Altro",
];

const Field = ({ label, value, onChange, type = "text", step, testid }) => (
  <div>
    <label className="text-[11px] text-slate-400 block mb-1">{label}</label>
    <input
      data-testid={testid}
      type={type}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full p-2 border border-slate-300 rounded-lg text-[12px] text-slate-800 outline-none focus:border-[#2b90ef]"
    />
  </div>
);

const ResultRow = ({ label, value, unit, strong, testid }) => (
  <div className="flex justify-between items-baseline py-1">
    <span className="text-[12px] text-emerald-900/80">{label}</span>
    <span className="text-[13px] text-emerald-950">
      <b className="font-display" data-testid={testid}>{value}</b>
      {unit ? <span className="text-[11px] text-emerald-900/60 ml-0.5">{unit}</span> : null}
    </span>
  </div>
);

export const Profilo = ({ profile, onProfileChange }) => {
  const [form, setForm] = useState(profile);

  const update = (patch) => {
    const next = { ...form, ...patch };
    setForm(next);
    onProfileChange(next);
  };

  const toggleAllergy = (a) => {
    const list = form.allergies || [];
    const has = list.includes(a);
    const allergies = has ? list.filter((x) => x !== a) : [...list, a];
    const patch = { allergies };
    if (a === "Altro" && has) patch.allergyOther = "";
    update(patch);
  };
  const otherSelected = (form.allergies || []).includes("Altro");

  const bmr = Math.round(computeBMR(form));
  const tdee = computeTDEE(form);
  const hydration = computeHydration(form.weight);
  const macros = computeMacroTargets(tdee);
  const abv = (ALCOHOL_TYPES.find((a) => a.value === form.drinkType) || ALCOHOL_TYPES[0]).abv;
  const alcohol = computeAlcohol(form.drinkVolume, abv);
  const area = MACRO_AREAS.find((a) => a.value === form.macroArea);

  const save = () => {
    onProfileChange(form);
    toast.success("Profilo salvato", {
      description: `BMR ${bmr} kcal · TDEE ${tdee} kcal (valori indicativi)`,
    });
  };

  return (
    <div className="w-full" data-testid="screen-progressi">
      <div className="text-center mb-4 mt-2">
        <h2 className="font-display text-xl font-bold text-slate-800">Il Tuo Profilo</h2>
        <p className="text-slate-400 text-xs mt-1">Calcolo nutrizionale in tempo reale</p>
      </div>

      {/* Filosofia */}
      <div
        className="rounded-2xl bg-[#e0f2fe] border border-sky-200 p-3.5 flex items-start gap-2.5 mb-4"
        data-testid="philosophy-note"
      >
        <HeartPulse size={18} className="text-[#2b90ef] shrink-0 mt-0.5" />
        <p className="text-[11px] leading-relaxed text-[#1e4e79]">
          Questa tecnologia supporta le tue scelte di benessere, ma il medico resta il tuo
          <b> consulente strategico per la salute</b>.
        </p>
      </div>

      {/* 1. Dati personali */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.02)] mb-3">
        <label className="text-[10px] text-slate-400 uppercase font-bold flex items-center gap-1.5 mb-2.5">
          <Weight size={12} /> Dati Personali
        </label>
        <div className="grid grid-cols-2 gap-2.5 mb-2.5">
          <div>
            <label className="text-[11px] text-slate-400 block mb-1">Sesso</label>
            <select
              data-testid="input-sex"
              value={form.sex}
              onChange={(e) => update({ sex: e.target.value })}
              className="w-full p-2 border border-slate-300 rounded-lg text-[12px] text-slate-800 bg-white outline-none"
            >
              <option value="M">Maschio</option>
              <option value="F">Femmina</option>
            </select>
          </div>
          <Field label="Età (anni)" type="number" value={form.age} onChange={(v) => update({ age: v })} testid="input-age" />
          <Field label="Peso (kg)" type="number" step="0.1" value={form.weight} onChange={(v) => update({ weight: v })} testid="input-weight" />
          <Field label="Altezza (cm)" type="number" value={form.height} onChange={(v) => update({ height: v })} testid="input-height" />
        </div>
        <label className="text-[11px] text-slate-400 flex items-center gap-1 mb-1"><Activity size={12} /> Livello di Attività Giornaliera</label>
        <select
          data-testid="select-activity"
          value={form.activity}
          onChange={(e) => update({ activity: e.target.value })}
          className="w-full p-2 border border-slate-300 rounded-lg text-[12px] text-slate-800 bg-white outline-none"
        >
          {ACTIVITY_LEVELS.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>
      </div>

      {/* 2. Profilo ed esigenze */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.02)] mb-3">
        <label className="text-[10px] text-slate-400 uppercase font-bold block mb-2.5">Profilo ed Esigenze Nutrizionali</label>
        <label className="text-[11px] text-slate-400 block mb-1">Scegli Macro Area Generale</label>
        <select
          data-testid="select-macro-area"
          value={form.macroArea}
          onChange={(e) => update({ macroArea: e.target.value, condition: "" })}
          className="w-full p-2 border border-slate-300 rounded-lg text-[12px] text-slate-800 bg-slate-50 outline-none mb-2.5"
        >
          <option value="">-- Seleziona un'area --</option>
          {MACRO_AREAS.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>
        <label className="text-[11px] text-slate-400 block mb-1">Scegli Condizione Specifica Dettagliata</label>
        <select
          data-testid="select-condition"
          value={form.condition}
          disabled={!area}
          onChange={(e) => update({ condition: e.target.value })}
          className="w-full p-2 border border-slate-300 rounded-lg text-[12px] text-slate-800 bg-slate-50 outline-none disabled:opacity-50"
        >
          <option value="">-- Seleziona la condizione specifica --</option>
          {area?.conditions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Preferenza alimentare / Dieta */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.02)] mb-3" data-testid="diet-section">
        <label className="text-[10px] text-slate-400 uppercase font-bold block mb-2.5">Preferenza Alimentare / Dieta</label>
        <div className="flex flex-wrap gap-2">
          {[
            { v: "", l: "Nessuna" },
            { v: "vegetariana", l: "Vegetariana" },
            { v: "vegana", l: "Vegana" },
          ].map((opt) => {
            const active = (form.diet || "") === opt.v;
            return (
              <button
                key={opt.v || "none"}
                onClick={() => update({ diet: opt.v })}
                data-testid={`diet-${opt.v || "none"}`}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors active:scale-95 ${
                  active
                    ? "bg-[#4f9e78] border-[#4f9e78] text-white"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:border-[#4f9e78]"
                }`}
              >
                {active && <Check size={12} />} {opt.l}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1">Riceverai un avviso <AlertTriangle size={11} className="text-[#f59e0b]" /> se scansioni un piatto non conforme alla dieta scelta.</p>
      </div>

      {/* Allergie a selezione multipla */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.02)] mb-3" data-testid="allergies-section">
        <label className="text-[10px] text-slate-400 uppercase font-bold block mb-2.5">Allergie e Intolleranze (selezione multipla)</label>
        <div className="flex flex-wrap gap-2">
          {COMMON_ALLERGENS.map((a) => {
            const active = (form.allergies || []).includes(a);
            return (
              <button
                key={a}
                onClick={() => toggleAllergy(a)}
                data-testid={`allergy-${a.split(" ")[0].toLowerCase()}`}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors active:scale-95 ${
                  active
                    ? "bg-[#2b90ef] border-[#2b90ef] text-white"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:border-[#2b90ef]"
                }`}
              >
                {active && <Check size={12} />} {a}
              </button>
            );
          })}
        </div>
        {otherSelected && (
          <input
            type="text"
            data-testid="allergy-other-input"
            value={form.allergyOther}
            onChange={(e) => update({ allergyOther: e.target.value })}
            placeholder="Scrivi la tua allergia..."
            className="w-full mt-3 p-2 border border-slate-300 rounded-lg text-[12px] text-slate-800 outline-none focus:border-[#2b90ef]"
          />
        )}
      </div>

      {/* 3. Calcolatore alcolici */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.02)] mb-3">
        <label className="text-[10px] text-slate-400 uppercase font-bold flex items-center gap-1.5 mb-2.5">
          <Wine size={12} /> Calcolatore Consumo Liquori / Alcolici
        </label>
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="text-[11px] text-slate-400 block mb-1">Tipo di Bevanda</label>
            <select
              data-testid="select-drink-type"
              value={form.drinkType}
              onChange={(e) => update({ drinkType: e.target.value })}
              className="w-full p-2 border border-slate-300 rounded-lg text-[12px] text-slate-800 bg-white outline-none"
            >
              {ALCOHOL_TYPES.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>
          <Field label="Quantità Consumata (ml)" type="number" value={form.drinkVolume} onChange={(v) => update({ drinkVolume: v })} testid="input-drink-volume" />
        </div>
      </div>

      {/* Risultati in tempo reale */}
      <div
        className="rounded-2xl bg-emerald-50 border-l-4 border-emerald-500 p-4 mb-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)]"
        data-testid="realtime-results"
      >
        <h3 className="font-display text-base font-bold text-emerald-900 mb-2 flex items-center gap-1.5">
          <Flame size={15} className="text-emerald-600" /> Risultati Profilo in Tempo Reale
        </h3>
        <ResultRow label="Metabolismo Basale (BMR)" value={bmr} unit="kcal" testid="result-bmr" />
        <ResultRow label="Fabbisogno Giornaliero (TDEE)" value={tdee} unit="kcal" testid="result-tdee" />
        <ResultRow label="Idratazione Minima Consigliata" value={hydration.toFixed(2)} unit="Litri/giorno" testid="result-hydration" />

        <p className="text-[11px] font-bold text-emerald-900 mt-3 mb-1">Target Macronutrienti Giornalieri:</p>
        <ResultRow label="Carboidrati" value={macros.carbs} unit="g" testid="result-carbs" />
        <ResultRow label="Grassi" value={macros.fat} unit="g" testid="result-fat" />
        <ResultRow label="Proteine" value={macros.protein} unit="g" testid="result-protein" />

        <p className="text-[11px] font-bold text-emerald-900 mt-3 mb-1">Impatto Alcolici / Liquori Inseriti:</p>
        <ResultRow label="Alcol Puro Ingerito" value={alcohol.grams} unit="g" testid="result-alcohol-g" />
        <ResultRow label="Calorie Vuote da Alcol" value={alcohol.kcal} unit="kcal" testid="result-alcohol-kcal" />
      </div>

      <button
        onClick={save}
        data-testid="save-profile-btn"
        className="w-full bg-[#2b90ef] hover:bg-[#1d78cc] text-white py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors active:scale-[0.98] mb-4"
      >
        <Save size={15} /> Salva Profilo
      </button>

      {/* Disclaimer legale */}
      <div
        className="rounded-2xl bg-amber-50 border border-amber-200 p-3.5 flex items-start gap-2.5"
        data-testid="legal-disclaimer"
      >
        <ShieldAlert size={18} className="text-[#f59e0b] shrink-0 mt-0.5" />
        <div className="text-left">
          <p className="text-[11px] font-bold text-[#78350f] mb-0.5 font-display uppercase tracking-wide flex items-center gap-1">
            <Info size={11} /> Disclaimer Legale
          </p>
          <p className="text-[11px] leading-relaxed text-[#92400e]">
            mangiaBene fornisce stime e informazioni a scopo puramente illustrativo, generate da IA, e
            non costituisce parere medico. Non sostituisce la consulenza di un medico o nutrizionista
            professionista. Consulta sempre uno specialista prima di modificare la tua dieta.
          </p>
        </div>
      </div>
    </div>
  );
};
